// MathInputModal — popover com MathLive (Cortex) que permite ao admin compor
// uma equação ou estrutura matemática/química de forma visual e retorna o
// LaTeX correspondente. O caller decide como inserir esse LaTeX no campo de
// texto que estiver focado.
//
// O LaTeX retornado vem sem delimitadores — o caller envolve em $...$
// (inline) ou $$...$$ (display) conforme o contexto. O modal expõe a opção
// "Modo bloco" e devolve essa flag no callback.
//
// UX:
//   - Math-field do MathLive no topo (o usuário também pode digitar atalhos
//     como x^2, \frac, \sqrt etc. direto)
//   - Toolbar de formatação (cor, negrito, itálico)
//   - Paletas categorizadas com símbolos de UM clique (gregos, conjuntos,
//     lógica, setas, operadores, geometria, química, funções)
//
// Usado pelo VisualBankEditor (admin) [[project-visual-bank-editor]].

import { useEffect, useRef, useState } from "preact/hooks";
import {
  Bold,
  Italic,
  Loader2,
  Palette,
  Sigma,
  X
} from "lucide-react";
import { ensureMathLive } from "@app/ui/lib/mathlive";

interface MathInputModalProps {
  initialLatex?: string;
  onCancel: () => void;
  onConfirm: (latex: string, display: boolean) => void;
}

// ===========================================================================
// Paleta de símbolos — agrupado por tema. `d` = display (UTF-8 ou rótulo
// curto), `l` = LaTeX inserido no math-field, `t` = tooltip opcional. Use
// `#0` no LaTeX para indicar onde o cursor cai depois da inserção (placeholder
// do MathLive).
// ===========================================================================

interface Sym {
  d: string;
  l: string;
  t?: string;
}

interface SymGroup {
  name: string;
  items: Sym[];
}

const GROUPS: SymGroup[] = [
  {
    name: "Comum",
    items: [
      { d: "x²", l: "#0^{2}", t: "Quadrado (sobrescrito)" },
      { d: "x³", l: "#0^{3}", t: "Cubo (sobrescrito)" },
      { d: "xⁿ", l: "#0^{#?}", t: "Sobrescrito" },
      { d: "x₂", l: "#0_{#?}", t: "Subscrito" },
      { d: "a⁄b", l: "\\frac{#?}{#?}", t: "Fração" },
      { d: "√", l: "\\sqrt{#?}", t: "Raiz quadrada" },
      { d: "ⁿ√", l: "\\sqrt[#?]{#?}", t: "Raiz n-ésima" },
      { d: "π", l: "\\pi" },
      { d: "e", l: "e" },
      { d: "∞", l: "\\infty", t: "Infinito" },
      { d: "°", l: "^{\\circ}", t: "Graus" },
      { d: "±", l: "\\pm" },
      { d: "∓", l: "\\mp" },
      { d: "×", l: "\\times" },
      { d: "÷", l: "\\div" },
      { d: "·", l: "\\cdot", t: "Ponto de multiplicação" },
      { d: "≠", l: "\\neq" },
      { d: "≤", l: "\\le" },
      { d: "≥", l: "\\ge" },
      { d: "≈", l: "\\approx" },
      { d: "≡", l: "\\equiv" },
      { d: "∝", l: "\\propto" }
    ]
  },
  {
    name: "Gregas (minúsculas)",
    items: [
      { d: "α", l: "\\alpha" },
      { d: "β", l: "\\beta" },
      { d: "γ", l: "\\gamma" },
      { d: "δ", l: "\\delta" },
      { d: "ε", l: "\\varepsilon" },
      { d: "ζ", l: "\\zeta" },
      { d: "η", l: "\\eta" },
      { d: "θ", l: "\\theta" },
      { d: "ι", l: "\\iota" },
      { d: "κ", l: "\\kappa" },
      { d: "λ", l: "\\lambda" },
      { d: "μ", l: "\\mu" },
      { d: "ν", l: "\\nu" },
      { d: "ξ", l: "\\xi" },
      { d: "π", l: "\\pi" },
      { d: "ρ", l: "\\rho" },
      { d: "σ", l: "\\sigma" },
      { d: "τ", l: "\\tau" },
      { d: "υ", l: "\\upsilon" },
      { d: "φ", l: "\\varphi" },
      { d: "χ", l: "\\chi" },
      { d: "ψ", l: "\\psi" },
      { d: "ω", l: "\\omega" }
    ]
  },
  {
    name: "Gregas (maiúsculas)",
    items: [
      { d: "Γ", l: "\\Gamma" },
      { d: "Δ", l: "\\Delta" },
      { d: "Θ", l: "\\Theta" },
      { d: "Λ", l: "\\Lambda" },
      { d: "Ξ", l: "\\Xi" },
      { d: "Π", l: "\\Pi" },
      { d: "Σ", l: "\\Sigma" },
      { d: "Υ", l: "\\Upsilon" },
      { d: "Φ", l: "\\Phi" },
      { d: "Ψ", l: "\\Psi" },
      { d: "Ω", l: "\\Omega" }
    ]
  },
  {
    name: "Conjuntos",
    items: [
      { d: "ℝ", l: "\\mathbb{R}", t: "Números reais" },
      { d: "ℤ", l: "\\mathbb{Z}", t: "Números inteiros" },
      { d: "ℕ", l: "\\mathbb{N}", t: "Números naturais" },
      { d: "ℚ", l: "\\mathbb{Q}", t: "Números racionais" },
      { d: "ℂ", l: "\\mathbb{C}", t: "Números complexos" },
      { d: "𝕂", l: "\\mathbb{K}", t: "Corpo genérico" },
      { d: "∅", l: "\\varnothing", t: "Conjunto vazio" },
      { d: "∈", l: "\\in", t: "Pertence" },
      { d: "∉", l: "\\notin", t: "Não pertence" },
      { d: "⊂", l: "\\subset", t: "Subconjunto" },
      { d: "⊆", l: "\\subseteq", t: "Subconjunto ou igual" },
      { d: "⊃", l: "\\supset" },
      { d: "⊇", l: "\\supseteq" },
      { d: "∪", l: "\\cup", t: "União" },
      { d: "∩", l: "\\cap", t: "Interseção" },
      { d: "∖", l: "\\setminus", t: "Diferença" },
      { d: "×", l: "\\times", t: "Produto cartesiano" }
    ]
  },
  {
    name: "Lógica",
    items: [
      { d: "∀", l: "\\forall", t: "Para todo" },
      { d: "∃", l: "\\exists", t: "Existe" },
      { d: "∄", l: "\\nexists", t: "Não existe" },
      { d: "¬", l: "\\neg", t: "Negação" },
      { d: "∧", l: "\\land", t: "E lógico" },
      { d: "∨", l: "\\lor", t: "Ou lógico" },
      { d: "⊕", l: "\\oplus", t: "Ou exclusivo" },
      { d: "⇒", l: "\\Rightarrow", t: "Implica" },
      { d: "⇐", l: "\\Leftarrow" },
      { d: "⇔", l: "\\Leftrightarrow", t: "Se e somente se" },
      { d: "≡", l: "\\equiv", t: "Equivalente" },
      { d: "≈", l: "\\approx", t: "Aproximadamente" },
      { d: "∴", l: "\\therefore", t: "Portanto" },
      { d: "∵", l: "\\because", t: "Porque" },
      { d: "⊤", l: "\\top", t: "Verdadeiro" },
      { d: "⊥", l: "\\bot", t: "Falso" }
    ]
  },
  {
    name: "Setas",
    items: [
      { d: "→", l: "\\to" },
      { d: "←", l: "\\leftarrow" },
      { d: "↔", l: "\\leftrightarrow" },
      { d: "⇒", l: "\\Rightarrow" },
      { d: "⇐", l: "\\Leftarrow" },
      { d: "⇔", l: "\\Leftrightarrow" },
      { d: "↑", l: "\\uparrow" },
      { d: "↓", l: "\\downarrow" },
      { d: "↕", l: "\\updownarrow" },
      { d: "↦", l: "\\mapsto", t: "Mapeia para" },
      { d: "⟶", l: "\\longrightarrow" },
      { d: "⟵", l: "\\longleftarrow" },
      { d: "⟷", l: "\\longleftrightarrow" },
      { d: "⇆", l: "\\rightleftarrows", t: "Setas de equilíbrio" },
      { d: "⇌", l: "\\rightleftharpoons", t: "Equilíbrio químico" },
      { d: "↗", l: "\\nearrow" },
      { d: "↘", l: "\\searrow" }
    ]
  },
  {
    name: "Operadores grandes",
    items: [
      { d: "∑", l: "\\sum_{#?}^{#?}{#?}", t: "Somatório com limites" },
      { d: "∏", l: "\\prod_{#?}^{#?}{#?}", t: "Produtório" },
      { d: "∫", l: "\\int_{#?}^{#?}{#?}\\,d#?", t: "Integral definida" },
      { d: "∫∫", l: "\\iint", t: "Integral dupla" },
      { d: "∮", l: "\\oint", t: "Integral de linha fechada" },
      { d: "⋃", l: "\\bigcup_{#?}^{#?}{#?}", t: "União grande" },
      { d: "⋂", l: "\\bigcap_{#?}^{#?}{#?}", t: "Interseção grande" },
      { d: "⨁", l: "\\bigoplus" },
      { d: "⨂", l: "\\bigotimes" },
      { d: "∂", l: "\\partial", t: "Derivada parcial" },
      { d: "∇", l: "\\nabla", t: "Nabla / Gradiente" },
      { d: "Δ", l: "\\Delta", t: "Delta / Variação" },
      { d: "lim", l: "\\lim_{#? \\to #?}{#?}", t: "Limite" }
    ]
  },
  {
    name: "Funções",
    items: [
      { d: "a⁄b", l: "\\frac{#?}{#?}", t: "Fração" },
      { d: "√", l: "\\sqrt{#?}", t: "Raiz quadrada" },
      { d: "ⁿ√", l: "\\sqrt[#?]{#?}", t: "Raiz n-ésima" },
      { d: "sen", l: "\\sen(#?)", t: "Seno (notação BR)" },
      { d: "cos", l: "\\cos(#?)" },
      { d: "tg", l: "\\tg(#?)", t: "Tangente (notação BR)" },
      { d: "cossec", l: "\\cossec(#?)", t: "Cossecante (notação BR)" },
      { d: "sec", l: "\\sec(#?)" },
      { d: "cotg", l: "\\cotg(#?)", t: "Cotangente (notação BR)" },
      { d: "arcsen", l: "\\arcsen(#?)" },
      { d: "arccos", l: "\\arccos(#?)" },
      { d: "arctg", l: "\\arctg(#?)" },
      { d: "log", l: "\\log_{#?}(#?)" },
      { d: "ln", l: "\\ln(#?)" },
      { d: "eˣ", l: "e^{#?}" },
      { d: "log₁₀", l: "\\log_{10}(#?)" },
      { d: "|x|", l: "\\left|#?\\right|", t: "Módulo / Valor absoluto" },
      { d: "⌊x⌋", l: "\\left\\lfloor #? \\right\\rfloor", t: "Piso (floor)" },
      { d: "⌈x⌉", l: "\\left\\lceil #? \\right\\rceil", t: "Teto (ceil)" },
      { d: "(ⁿₖ)", l: "\\binom{#?}{#?}", t: "Combinação binomial" },
      { d: "n!", l: "#0!", t: "Fatorial" },
      { d: "→", l: "\\vec{#?}", t: "Vetor" },
      { d: "⟨,⟩", l: "\\langle #? \\rangle", t: "Produto interno" },
      { d: "𝐌", l: "\\begin{pmatrix} #? & #? \\\\ #? & #? \\end{pmatrix}", t: "Matriz 2x2" }
    ]
  },
  {
    name: "Geometria",
    items: [
      { d: "△", l: "\\triangle", t: "Triângulo" },
      { d: "□", l: "\\square", t: "Quadrado" },
      { d: "○", l: "\\circ", t: "Círculo / Composição" },
      { d: "∠", l: "\\angle", t: "Ângulo" },
      { d: "∡", l: "\\measuredangle", t: "Ângulo medido" },
      { d: "°", l: "^{\\circ}", t: "Graus" },
      { d: "⊥", l: "\\perp", t: "Perpendicular" },
      { d: "∥", l: "\\parallel", t: "Paralelo" },
      { d: "≅", l: "\\cong", t: "Congruente" },
      { d: "∼", l: "\\sim", t: "Semelhante" },
      { d: "→", l: "\\overrightarrow{#?}", t: "Vetor / Reta orientada" },
      { d: "↔", l: "\\overleftrightarrow{#?}", t: "Reta" },
      { d: "⌒", l: "\\frown", t: "Arco" }
    ]
  },
  {
    name: "Química",
    items: [
      { d: "→", l: "\\to", t: "Reação direta" },
      { d: "⇌", l: "\\rightleftharpoons", t: "Equilíbrio reversível" },
      { d: "⇄", l: "\\rightleftarrows", t: "Reação reversível" },
      { d: "↑", l: "\\uparrow", t: "Gás liberado" },
      { d: "↓", l: "\\downarrow", t: "Precipitado" },
      { d: "Δ", l: "\\Delta", t: "Aquecimento" },
      { d: "H₂O", l: "H_{2}O" },
      { d: "CO₂", l: "CO_{2}" },
      { d: "Fe²⁺", l: "Fe^{2+}", t: "Cátion" },
      { d: "Cl⁻", l: "Cl^{-}", t: "Ânion" },
      { d: "H⁺", l: "H^{+}" },
      { d: "e⁻", l: "e^{-}", t: "Elétron" },
      { d: "Aˣ", l: "#0^{#?}", t: "Carga / sobrescrito" },
      { d: "Aₙ", l: "#0_{#?}", t: "Índice / subscrito" }
    ]
  },
  {
    name: "Símbolos",
    items: [
      { d: "•", l: "\\bullet" },
      { d: "∘", l: "\\circ" },
      { d: "★", l: "\\star" },
      { d: "†", l: "\\dagger" },
      { d: "‡", l: "\\ddagger" },
      { d: "§", l: "\\S" },
      { d: "¶", l: "\\P" },
      { d: "ℏ", l: "\\hbar", t: "h-cortado (Planck reduzida)" },
      { d: "ℓ", l: "\\ell" },
      { d: "ℵ", l: "\\aleph" },
      { d: "ℑ", l: "\\Im", t: "Parte imaginária" },
      { d: "ℜ", l: "\\Re", t: "Parte real" },
      { d: "℘", l: "\\wp" },
      { d: "...", l: "\\ldots", t: "Reticências baixas" },
      { d: "⋯", l: "\\cdots", t: "Reticências centradas" },
      { d: "⋮", l: "\\vdots", t: "Reticências verticais" },
      { d: "⋱", l: "\\ddots", t: "Reticências diagonais" }
    ]
  }
];

// Cores prontas — clicar pinta a próxima inserção (ou seleção).
const COLOR_PRESETS: Array<{ name: string; latex: string; hex: string }> = [
  { name: "Padrão", latex: "", hex: "" },
  { name: "Vermelho", latex: "red", hex: "#dc2626" },
  { name: "Violeta", latex: "violet", hex: "#8b5cf6" },
  { name: "Azul", latex: "blue", hex: "#2563eb" },
  { name: "Verde", latex: "green", hex: "#16a34a" },
  { name: "Âmbar", latex: "orange", hex: "#f59e0b" },
  { name: "Rosa", latex: "magenta", hex: "#db2777" },
  { name: "Cinza", latex: "gray", hex: "#6b7280" }
];

export function MathInputModal({
  initialLatex,
  onCancel,
  onConfirm
}: MathInputModalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mathRef = useRef<HTMLElement | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [latex, setLatex] = useState<string>(initialLatex ?? "");
  const [display, setDisplay] = useState<boolean>(false);
  const [activeGroup, setActiveGroup] = useState<string>(GROUPS[0].name);

  useEffect(() => {
    let cancelled = false;
    ensureMathLive()
      .then(() => {
        if (cancelled) return;
        const host = containerRef.current;
        if (!host) return;
        const field = document.createElement("math-field");
        field.style.fontSize = "22px";
        field.style.minHeight = "120px";
        field.style.padding = "12px";
        field.style.width = "100%";
        field.style.border = "1px solid hsl(var(--border))";
        field.style.borderRadius = "6px";
        field.style.background = "hsl(var(--background))";
        if (initialLatex) (field as any).value = initialLatex;
        field.addEventListener("input", () => {
          setLatex(((field as any).value as string) ?? "");
        });
        host.innerHTML = "";
        host.appendChild(field);
        mathRef.current = field;
        setTimeout(() => (field as any).focus?.(), 50);
        setPhase("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setErrMsg(err instanceof Error ? err.message : "Falha ao carregar editor de equações.");
        setPhase("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ESC fecha. Click fora NÃO fecha [[feedback-modal-no-click-outside-close]].
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  function executeInsert(latexSnippet: string) {
    const mf: any = mathRef.current;
    if (!mf) return;
    try {
      mf.executeCommand?.(["insert", latexSnippet]);
    } catch {
      try {
        mf.insert?.(latexSnippet);
      } catch {
        /* fallback silencioso */
      }
    }
    mf.focus?.();
    setLatex((mf.value as string) ?? "");
  }

  function applyWrap(commandLatex: string) {
    // Comando que envolve o conteúdo selecionado (ou cria placeholder) —
    // ex.: "\\mathbf{#@}" onde #@ é a seleção atual.
    executeInsert(commandLatex);
  }

  function applyColor(colorLatex: string) {
    if (!colorLatex) return; // "Padrão" = sem cor
    applyWrap(`\\textcolor{${colorLatex}}{#@}`);
  }

  function handleConfirm() {
    const value = mathRef.current ? ((mathRef.current as any).value as string) : latex;
    if (!value || !value.trim()) {
      onCancel();
      return;
    }
    onConfirm(value.trim(), display);
  }

  const group = GROUPS.find((g) => g.name === activeGroup) ?? GROUPS[0];

  return (
    <div class="fixed inset-0 z-[120] grid place-items-center bg-background/80 p-3 backdrop-blur-sm">
      <div class="flex w-full max-w-4xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Sigma className="size-5 text-primary" />
            Inserir equação
          </h2>
          <button
            type="button"
            onClick={onCancel}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>

        <div class="grid max-h-[78vh] grid-cols-1 gap-3 overflow-y-auto p-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          {/* Coluna esquerda: math-field + cor/negrito/itálico + LaTeX gerado */}
          <div class="space-y-3">
            <p class="text-[11px] text-muted-foreground">
              Componha visualmente — clique nos botões da direita, use a barra
              de fórmulas do próprio editor, ou digite atalhos
              (<code class="rounded bg-accent px-1">x^2</code>,{" "}
              <code class="rounded bg-accent px-1">\frac</code>,{" "}
              <code class="rounded bg-accent px-1">\sqrt</code> etc.). A equação
              vira KaTeX e é renderizada para o aluno com os macros BR (sen, tg,
              cossec, cotg, arcsen, mdc, mmc).
            </p>

            {phase === "loading" && (
              <div class="grid h-40 place-items-center rounded-md border border-dashed border-border text-sm text-muted-foreground">
                <span class="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" /> Carregando editor…
                </span>
              </div>
            )}
            {phase === "error" && (
              <div class="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {errMsg ?? "Falha ao carregar editor de equações."}
              </div>
            )}

            <div ref={containerRef} class={phase !== "ready" ? "hidden" : ""} />

            {phase === "ready" && (
              <>
                {/* Toolbar formato — cor, negrito, itálico */}
                <div class="space-y-2 rounded-md border border-border bg-background/50 p-2.5">
                  <div class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <Palette className="size-3" />
                    Formato
                  </div>
                  <div class="flex flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => applyWrap("\\mathbf{#@}")}
                      title="Negrito (envolve a seleção ou cria placeholder)"
                      aria-label="Negrito"
                      class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-bold hover:bg-accent"
                    >
                      <Bold className="size-3" /> Negrito
                    </button>
                    <button
                      type="button"
                      onClick={() => applyWrap("\\mathit{#@}")}
                      title="Itálico"
                      aria-label="Itálico"
                      class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] italic hover:bg-accent"
                    >
                      <Italic className="size-3" /> Itálico
                    </button>
                    <button
                      type="button"
                      onClick={() => applyWrap("\\text{#?}")}
                      title="Texto comum (sem itálico, para palavras)"
                      class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:bg-accent"
                    >
                      Texto
                    </button>
                    <button
                      type="button"
                      onClick={() => applyWrap("\\mathbb{#@}")}
                      title="Blackboard (números reais / inteiros etc.)"
                      class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:bg-accent"
                    >
                      ℝℤℕ
                    </button>
                    <button
                      type="button"
                      onClick={() => applyWrap("\\overline{#@}")}
                      title="Linha em cima (média, complemento)"
                      class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:bg-accent"
                    >
                      x̄
                    </button>
                    <button
                      type="button"
                      onClick={() => applyWrap("\\hat{#@}")}
                      title="Chapéu (estimador, vetor unitário)"
                      class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:bg-accent"
                    >
                      x̂
                    </button>
                  </div>
                  <div class="flex flex-wrap items-center gap-1">
                    {COLOR_PRESETS.map((c) => (
                      <button
                        key={c.name}
                        type="button"
                        onClick={() => applyColor(c.latex)}
                        title={c.name}
                        aria-label={`Cor ${c.name}`}
                        class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-1.5 py-0.5 text-[10px] hover:bg-accent"
                      >
                        {c.hex ? (
                          <span
                            class="inline-block size-3 rounded-sm border border-border"
                            style={{ backgroundColor: c.hex }}
                          />
                        ) : (
                          <span class="inline-block size-3 rounded-sm border border-dashed border-border" />
                        )}
                        {c.name}
                      </button>
                    ))}
                  </div>
                </div>

                <details class="rounded-md border border-border bg-background/50 px-3 py-2 text-xs">
                  <summary class="cursor-pointer font-semibold text-muted-foreground">
                    LaTeX gerado
                  </summary>
                  <pre class="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px] text-foreground">
                    {latex || "(vazio)"}
                  </pre>
                </details>

                <label class="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={display}
                    onChange={(e) =>
                      setDisplay((e.target as HTMLInputElement).checked)
                    }
                    class="size-4"
                  />
                  Modo bloco (display) — equação em linha própria, centralizada.
                  Desligado = inline com o texto.
                </label>
              </>
            )}
          </div>

          {/* Coluna direita: paleta com tabs categorizadas */}
          {phase === "ready" && (
            <div class="space-y-2">
              <div class="flex flex-wrap items-center gap-1 border-b border-border pb-2">
                {GROUPS.map((g) => (
                  <button
                    key={g.name}
                    type="button"
                    onClick={() => setActiveGroup(g.name)}
                    class={`rounded-md px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                      g.name === activeGroup
                        ? "bg-primary text-white"
                        : "border border-border bg-background text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {g.name}
                  </button>
                ))}
              </div>
              <div class="grid grid-cols-5 gap-1.5 sm:grid-cols-6">
                {group.items.map((s, i) => (
                  <button
                    key={`${group.name}-${i}-${s.l}`}
                    type="button"
                    onClick={() => executeInsert(s.l)}
                    title={s.t ? `${s.t} — ${s.l}` : s.l}
                    aria-label={s.t ?? s.l}
                    class="grid h-10 place-items-center rounded-md border border-border bg-background text-base font-medium hover:border-primary hover:bg-accent/40 focus:outline-none focus-visible:border-primary"
                  >
                    {s.d}
                  </button>
                ))}
              </div>
              <p class="text-[10px] text-muted-foreground">
                Clique para inserir no cursor. <code>#?</code> deixa um
                placeholder onde o cursor cai depois — útil em frações, raízes
                e integrais. <code>#@</code> envolve a seleção atual.
              </p>
            </div>
          )}
        </div>

        <div class="flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
          <button
            type="button"
            onClick={onCancel}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={phase !== "ready" || !latex.trim()}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
          >
            Inserir
          </button>
        </div>
      </div>
    </div>
  );
}
